<template>
  <nav
    :class="
      cn(
        'sticky top-0 z-10 flex w-full flex-col items-center bg-white',
        { 'md:border-b': isSticky },
        classes?.root
      )
    "
  >
    <div :class="cn('container flex h-20 w-full items-center md:h-14 md:px-0', classes?.container)">
      <!-- Start section -->
      <div :class="cn('flex items-center', classes?.start)">
        <template v-if="showBackButton">
          <slot name="back">
            <UiButton
              v-bind="backProps"
              :class="cn('min-w-10 rounded-none p-0 md:flex md:h-14', classes?.back)"
              :variant="isMobile ? 'ghost' : 'link'"
            >
              <slot name="backIcon">
                <LucideChevronLeft class="translate-x-px rotate-180" :class="classes?.backIcon" />
              </slot>
            </UiButton>
          </slot>
        </template>

        <slot name="logo">
          <UiButton
            v-if="showLogo"
            v-bind="logoProps"
            :class="
              cn('h-fit p-0 transition-all duration-300 md:h-14 md:ps-2', classes?.logo, {
                'md:ps-5': !showBackButton,
              })
            "
            variant="link"
            :id="genClickId('kyc-nav', 'logo')"
          >
            <slot name="logoIcon">
              <IconFinqLogo :size="24" :class="classes?.logoIcon" />
            </slot>
          </UiButton>
        </slot>
      </div>

      <!-- Center section -->
      <slot name="center">
        <!-- Default slot for progress bar or other center content -->
      </slot>

      <!-- End section -->
      <div :class="cn('ms-auto', classes?.end)">
        <slot name="cta">
          <UiButton
            v-if="showExitButton"
            v-bind="exitButtonProps"
            :class="cn('md:h-14 md:rounded-none md:border-none md:p-5', classes?.cta)"
            variant="outline"
            @click="handleExit"
          >
            {{ exitButtonText || t("save_and_quit") }}
          </UiButton>
        </slot>
      </div>
    </div>

    <!-- Additional content slot -->
    <slot />
  </nav>
</template>

<script setup lang="ts">
import { LucideChevronLeft } from "lucide-vue-next"

import type { UiButtonProps } from "@finq/ui/components/ui/Button.vue"

interface NavbarClasses {
  root?: ClassValue
  container?: ClassValue
  logo?: ClassValue
  logoIcon?: ClassValue
  back?: ClassValue
  backIcon?: ClassValue
  start?: ClassValue
  end?: ClassValue
  cta?: ClassValue
}

export interface KycBaseNavbarProps {
  classes?: NavbarClasses
  backProps?: UiButtonProps
  logoProps?: UiButtonProps
  exitButtonProps?: UiButtonProps
  showBackButton?: boolean
  showLogo?: boolean
  showExitButton?: boolean
  exitButtonText?: string
  isSticky?: boolean
}

const props = withDefaults(defineProps<KycBaseNavbarProps>(), {
  showBackButton: true,
  showLogo: true,
  showExitButton: true,
})

const emit = defineEmits<{
  (e: "exit"): void
}>()

const { isMobile } = useDisplay()
const { scrollPos } = useScrollUtils()
const { t } = useI18n({ useScope: "local" })

const isSticky = computed(() => {
  return props.isSticky || scrollPos.value > 1
})

function handleExit() {
  emit("exit")
}

// Helper to generate click IDs for analytics
function genClickId(...parts: string[]) {
  return parts.join("-")
}
</script>

<i18n lang="json">
{
  "he": {
    "save_and_quit": "שמירה ויציאה"
  },
  "en": {
    "save_and_quit": "Save and quit"
  }
}
</i18n>
